import { Dataset } from '@/shared/Charting/LineChart/lineChart.types.ts'
import TagWithCloseIcon from '@/shared/Tags/TagWithCloseIcon'
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import AddParameterTag from '@/shared/Charting/LineChart/plugins/LineChartTSDBDataRendererPlugin/AddParameterTag'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'
import Tag from '@/shared/Tags/Tag'

interface IProps {
  datasets: Dataset[]
  onParameterRemove?: (p: IParameter['id']) => void
  onParameterAdd?: (p: IParameter['id']) => void
}

const GAP_PX = 8

const LineChartDatasetsTagsLine = (props: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const showMoreTextMeasureRef = useRef<HTMLSpanElement>(null)

  const [isExpanded, setIsExpanded] = useState(false)
  const [renderLimit, setRenderLimit] = useState(() => props.datasets.length)

  const computeAndSetRenderLimit = useCallback(() => {
    if (isExpanded) return

    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const containerWidth = container.clientWidth
    if (!containerWidth) return

    const datasetNodes = Array.from(measure.querySelectorAll<HTMLElement>('[data-role="dataset-tag-measure"]'))
    const datasetWidths = datasetNodes.map((node) => node.getBoundingClientRect().width)

    const prefixSums: number[] = [0]
    for (const w of datasetWidths) {
      prefixSums.push(prefixSums[prefixSums.length - 1] + w)
    }

    const addWidth = props.onParameterAdd
      ? measure.querySelector<HTMLElement>('[data-role="add-tag-measure"]')?.getBoundingClientRect().width || 0
      : 0

    const totalTags = datasetWidths.length
    const addItems = props.onParameterAdd ? 1 : 0

    const totalItemsAll = totalTags + addItems
    const totalWidthAll = prefixSums[totalTags] + addWidth + Math.max(totalItemsAll - 1, 0) * GAP_PX
    if (totalWidthAll <= containerWidth) {
      setRenderLimit((prev) => (prev === totalTags ? prev : totalTags))
      return
    }

    const showMoreWrapper = measure.querySelector<HTMLElement>('[data-role="show-more-tag-measure"]')
    if (!showMoreWrapper || !showMoreTextMeasureRef.current) {
      const fallbackLimit = Math.max(totalTags - 1, 0)
      setRenderLimit((prev) => (prev === fallbackLimit ? prev : fallbackLimit))
      return
    }

    for (let limit = totalTags - 1; limit >= 0; limit--) {
      const hiddenCount = totalTags - limit
      showMoreTextMeasureRef.current.textContent = `+${hiddenCount}`

      const showMoreWidth = showMoreWrapper.getBoundingClientRect().width

      const items = limit + addItems + 1 // tags + add + showMore
      const totalWidth = prefixSums[limit] + addWidth + showMoreWidth + Math.max(items - 1, 0) * GAP_PX

      if (totalWidth <= containerWidth) {
        setRenderLimit((prev) => (prev === limit ? prev : limit))
        return
      }
    }

    setRenderLimit(0)
  }, [isExpanded, props.datasets, props.onParameterAdd, props.onParameterRemove])

  useLayoutEffect(() => {
    computeAndSetRenderLimit()
  }, [computeAndSetRenderLimit])

  useEffect(() => {
    const element = containerRef.current
    if (!element || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => computeAndSetRenderLimit())
    observer.observe(element)
    return () => observer.disconnect()
  }, [computeAndSetRenderLimit])

  useEffect(() => {
    if (renderLimit > props.datasets.length) {
      setRenderLimit(props.datasets.length)
    }
  }, [props.datasets.length, renderLimit])

  const datasets = useMemo(() => {
    if (isExpanded) return props.datasets
    return props.datasets.slice(0, renderLimit)
  }, [isExpanded, props.datasets, renderLimit])

  const hiddenCount = useMemo(() => {
    if (isExpanded) return 0
    return Math.max(props.datasets.length - renderLimit, 0)
  }, [isExpanded, props.datasets.length, renderLimit])

  return (
    <>
      <div
        className="inline-flex-gap gap8 width100"
        style={{
          flexWrap: isExpanded ? 'wrap' : 'nowrap',
        }}
        ref={containerRef}
      >
        {datasets.map((s) => {
          const removableParameterId = typeof s.id === 'number' ? s.id : null

          return (
            <div key={s.label} style={{ flexShrink: 0 }}>
              {props.onParameterRemove && removableParameterId !== null ? (
                <TagWithCloseIcon onClose={() => props.onParameterRemove(removableParameterId)} pureColor={s.color}>
                  {s.label}
                </TagWithCloseIcon>
              ) : (
                <Tag pureColor={s.color}>{s.label}</Tag>
              )}
            </div>
          )
        })}

        {props.onParameterAdd && (
          <div style={{ flexShrink: 0 }}>
            <AddParameterTag
              dropdownPosition={props.datasets.length > 1 ? 'left' : 'right'}
              onParameterAdd={props.onParameterAdd}
            />
          </div>
        )}

        {isExpanded ? (
          <div style={{ flexShrink: 0 }}>
            <Tag color="gray" style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(false)}>
              Скрыть
            </Tag>
          </div>
        ) : (
          hiddenCount > 0 && (
            <div style={{ flexShrink: 0 }}>
              <Tag color="gray" style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(true)}>
                +{hiddenCount}
              </Tag>
            </div>
          )
        )}
      </div>

      <div
        aria-hidden={true}
        style={{
          position: 'absolute',
          top: '-10000px',
          left: '-10000px',
          pointerEvents: 'none',
          visibility: 'hidden',
        }}
        ref={measureRef}
      >
        <div className="inline-flex-gap gap8">
          {props.datasets.map((s) => {
            const removableParameterId = typeof s.id === 'number' ? s.id : null

            return (
              <div key={s.label} data-role="dataset-tag-measure">
                {props.onParameterRemove && removableParameterId !== null ? (
                  <TagWithCloseIcon onClose={() => undefined} pureColor={s.color}>
                    {s.label}
                  </TagWithCloseIcon>
                ) : (
                  <Tag pureColor={s.color}>{s.label}</Tag>
                )}
              </div>
            )
          })}

          {props.onParameterAdd && (
            <div data-role="add-tag-measure">
              <Tag color="doted" style={{ width: '110px', textAlign: 'center' }}>
                + Добавить
              </Tag>
            </div>
          )}

          <div data-role="show-more-tag-measure">
            <Tag color="gray">
              <span ref={showMoreTextMeasureRef}>+0</span>
            </Tag>
          </div>
        </div>
      </div>
    </>
  )
}

export default memo(LineChartDatasetsTagsLine)
